import { Redirect, Route, Switch, useLocation, useParams } from "react-router-dom";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Login } from "./Login";
import { Signup, SellerSignup } from "./Signup";
import ProtectedRoute from "../components/ProtectedRoute";
import { Navigation } from "../components/navigation";
import { DataRoom } from "./data_room";
import { DataRoomFile } from "./data_room/data_room_file";
import { Access } from "./access";
import { Invite } from "./Invite";
import { Activity } from "./data_room/Activity";
import { Questions } from "./questions";
import { Home } from "./home";
import { CreateDealFlow } from "./create_deal";
import { Firm } from "./firm";
import { FirmInvite } from "./firm/Invite";
import { Settings } from "./settings";
import { DealProtection } from "./DealProtection";
import { Dashboard } from "./dashboard";
import { DealSettings } from "./deal/settings";
import { DealTasks, DealTaskDetails } from "./deal/tasks";
import { OnboardingGate } from "./OnboardingGate";
import { AccountGate } from "./TokenGate";
import { Ddql } from "./questions/ddql";
import { Archived } from "./home/Archived";
import { Onboarding } from "./onboarding";
import { DisplayQuestion } from "./questions/Question";
import { QuestionClusters } from "./questions/clusters";
import { DealNavigation } from "../components/deal_navigation";
import { Assistant } from "../components/deal_navigation/assistant";
import { DealNavigationTabs } from "../components/deal_navigation/tabs";
import { assistantSelectors } from "../store/assistant/selector";
import { authSelectors } from "../store/auth/selector";
import { actions as authActions } from "../store/auth/slice";
import { AccountPurposeType } from "../graphql/generated";
import { Seller } from "./seller";
import { SellerNavbar } from "../components/seller_navbar";
import { Pipeline } from "./pipeline";
import { UploadContextProvider } from "../components/data_room/file_uploaders/useUploadContext";
import { UploadProgressToast } from "../components/data_room/file_uploaders/UploadProgressToast";

const Routes = () => {
  return (
    <>
      <Switch>
        <ProtectedRoute path="/login" onlyUnAuthenticated>
          <Login />
        </ProtectedRoute>
        <ProtectedRoute path="/seller-signup" onlyUnAuthenticated>
          <SellerSignup />
        </ProtectedRoute>
        <ProtectedRoute path="/signup" onlyUnAuthenticated>
          <Signup />
        </ProtectedRoute>

        <Route path="/deal-invite/:token">
          <Invite />
        </Route>

        <Route path="/firm-invite/:token">
          <FirmInvite />
        </Route>

        <OnboardingGate>
          <AccountGate>
            <ProtectedRoute path="/">
              <AuthedRoot />
            </ProtectedRoute>
          </AccountGate>
        </OnboardingGate>
      </Switch>
    </>
  );
};

const AuthedRoot = () => {
  const location = useLocation();
  const account = useSelector(authSelectors.account);
  const isPipeline = account?.accountPurposeType === AccountPurposeType.Pipeline;

  if (isPipeline) {
    const firstDealId = account?.deals?.[0]?.id;
    return (
      <div className="flex flex-1 flex-col min-w-0">
        <SellerNavbar />
        <Switch>
          <Route path="/seller/:dealId">
            <Seller />
          </Route>
          {firstDealId && <Redirect to={`/seller/${firstDealId}`} />}
          <Redirect to="/seller" />
        </Switch>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden md:flex-row">
      <Route path="/" exact>
        <Redirect to={"/deals"} />
      </Route>

      {location.pathname.startsWith("/onboarding") ? null : <Navigation />}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-persian-50">
        <div className="flex max-w-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <Route path="/onboarding">
            <Onboarding />
          </Route>
          <Route path="/deals" exact>
            <Home />
          </Route>

          <Route path="/deals/archived">
            <Archived />
          </Route>

          {/* <Route path="/tasks">
            <TasksParent />
          </Route> */}

          <Route path="/create-deal">
            <CreateDealFlow />
          </Route>

          <Route path="/firm">
            <Firm />
          </Route>

          <Route path="/settings">
            <Settings />
          </Route>
          <Route path="/pipeline">
            <InternalRoute>
              <Pipeline />
            </InternalRoute>
          </Route>
          <Route path="/deal/:dealId">
            <DealProtection>
              <DealRouteLayout />
            </DealProtection>
          </Route>
        </div>
      </div>
    </div>
  );
};

function InternalRoute({ children }: { children: React.ReactNode }) {
  const account = useSelector(authSelectors.account);
  const isInternal = account?.email?.endsWith("@liquidacquire.com");
  if (!isInternal) return <Redirect to="/" />;
  return <>{children}</>;
}

const DealRouteLayout = () => {
  const [showAssistant, setShowAssistant] = useState(false);
  const dispatch = useDispatch();
  const assistantDisplayMode = useSelector(
    assistantSelectors.assistantDisplayMode,
  );
  const navigationCollapsed = useSelector(authSelectors.navigationCollapsed);
  const showInlineAssistant = showAssistant && assistantDisplayMode === "panel";
  const prevNavCollapsed = useRef(navigationCollapsed);

  useEffect(() => {
    const wasCollapsed = prevNavCollapsed.current;
    prevNavCollapsed.current = navigationCollapsed;

    if (!showInlineAssistant) return;

    if (wasCollapsed && !navigationCollapsed) {
      setShowAssistant(false);
    } else if (!navigationCollapsed) {
      dispatch(authActions.setNavigationCollapsed({ collapsed: true }));
    }
  }, [dispatch, navigationCollapsed, showInlineAssistant]);

  return (
    <div className="flex min-w-0 flex-1 overflow-hidden">
      <div className="min-w-0 flex-1 flex flex-col overflow-hidden">
        <DealNavigation
          showAssistant={showAssistant}
          setShowAssistant={setShowAssistant}
          withPanelOpen={showInlineAssistant}
        />
        <div
          className={`flex flex-col flex-1 overflow-hidden my-2 bg-white shadow ${
            showInlineAssistant ? "mx-0 rounded-none" : "mx-6 rounded-lg"
          }`}
        >
          <DealNavigationTabs />
          <div className="flex-1 overflow-y-auto min-h-0">
            <RoutesRoot />
          </div>
        </div>
      </div>
      <Assistant
        open={showAssistant}
        onClose={() => setShowAssistant(false)}
      />
    </div>
  );
};

const RoutesRoot = () => {
  return (
    <Switch>
      <Route path="/deal/:dealId/dashboard">
        <Dashboard />
      </Route>

      {/* <Route path="/buyers/" exact>
          <Buyers />
        </Route> */}

      {/* <Route path="/buyers/dd-checklists" exact>
          <DDChecklists />
        </Route>

        <Route path="/buyers/dd-checklists/:id">
          <DDChecklist />
        </Route>

        <Route path="/buyers/:id">
          <Buyer />
        </Route> */}

      <Route path="/deal/:dealId/questions/lists/:id">
        <Ddql />
      </Route>

      <Route path="/deal/:dealId/questions/clusters">
        <QuestionClusters />
      </Route>

      <Route path="/deal/:dealId/questions/:id">
        <DisplayQuestion />
      </Route>

      <Route path="/deal/:dealId/questions">
        <Questions />
      </Route>

      <Route path="/deal/:dealId/tasks/:taskId">
        <DealTaskDetails />
      </Route>

      <Route path="/deal/:dealId/tasks">
        <DealTasks />
      </Route>

      {/* <Route path="/errors" exact>
          <Errors />
        </Route>

        <Route path="/errors/:id">
          <ErrorDetails />
        </Route> */}

      <Route path="/deal/:dealId/access">
        <Access />
      </Route>

      <Route path="/deal/:dealId/settings">
        <DealSettings />
      </Route>

      <Route path="/deal/:dealId/documents">
        <DocumentsUploadRoutes />
      </Route>
    </Switch>
  );
};

const DocumentsUploadRoutes = () => {
  const { dealId } = useParams<{ dealId: string }>();

  return (
    <UploadContextProvider navigationScopePath={`/deal/${dealId}/documents`}>
      <UploadProgressToast />
      <Switch>
        <Route path="/deal/:dealId/documents/folder/:folderId/file/:id/versions">
          <DataRoomFile />
        </Route>
        <Route path="/deal/:dealId/documents/folder/:folderId/file/:id" exact>
          <DataRoomFile />
        </Route>
        <Route path="/deal/:dealId/documents/folder/:folderId">
          <DataRoom />
        </Route>
        <Route path="/deal/:dealId/documents/activity" exact>
          <Activity />
        </Route>
        <Route path="/deal/:dealId/documents" exact>
          <DataRoom />
        </Route>
        <Redirect to="/deal/:dealId/documents" />
      </Switch>
    </UploadContextProvider>
  );
};

export default Routes;
